import { memo, useCallback, useState } from "react";
import { Box, Text, Spinner, Title } from "@repo/ui";
import { useTheme } from "@repo/tokens";
import { useTranslation } from "react-i18next";
import {
  AssetRenderer,
  type AssetEvent,
  type AssetError,
  type QuestionAssetTypeProps,
  type FetchAssetFilter,
  type ThemeName as AssetThemeName,
} from "@repo/asset-rendering";
import { QuestionHeader } from "../QuestionHeader";
import { useModuleData } from "../../context/ModuleDataContext";
import styles from "./QuestionRenderer.module.scss";

const ASSET_THEME_MAP: Record<number, AssetThemeName> = {
  1: "LEARN",
  2: "META",
  3: "SCA",
};

export interface QuestionRendererProps {
  assetId: string;
  questionNumber: number;
  authenticationToken: string | null;
  env?: string;
  assetTypeProps?: QuestionAssetTypeProps;
  fetchAssetFilter?: FetchAssetFilter;
  onEvent?: (event: AssetEvent) => void;
  onError?: (error: AssetError) => void;
  onClearSelection?: () => void;
  hasSelection?: boolean;
  isPanelOpen?: boolean;
  onTogglePanel?: () => void;
  isListOpen?: boolean;
  onToggleList?: () => void;
  onFlagToggle?: () => void;
  clearRevision?: number;
  hideControls?: boolean;
  showTextView?: boolean;
  showGridView?: boolean;
  isClearDisabled?: boolean;
  hideClearSelection?: boolean;
}

export const QuestionRenderer = memo(
  ({
    assetId,
    questionNumber,
    authenticationToken,
    env,
    assetTypeProps,
    fetchAssetFilter,
    onEvent,
    onError,
    onClearSelection,
    hasSelection = false,
    isPanelOpen = false,
    onTogglePanel,
    isListOpen = false,
    onToggleList,
    onFlagToggle,
    clearRevision = 0,
    hideControls = false,
    showTextView = true,
    showGridView = true,
    isClearDisabled = false,
    hideClearSelection = false,
  }: QuestionRendererProps) => {
    const { theme } = useTheme();
    const { t } = useTranslation("tests");
    const { profilesData } = useModuleData();
    const verticalId =
      profilesData?.unified_account_details?.profiles?.[0]?.vertical_id;
    const assetThemeName: AssetThemeName =
      (verticalId && ASSET_THEME_MAP[verticalId]) || "LEARN";
    const [hasError, setHasError] = useState(false);

    const handleError = useCallback(
      (error: AssetError) => {
        setHasError(true);
        onError?.(error);
      },
      [onError, assetId],
    );

    return (
      <Box className={styles.questionContainer}>
        <QuestionHeader
          questionNumber={questionNumber}
          onClearSelection={onClearSelection}
          hasSelection={hasSelection}
          isPanelOpen={isPanelOpen}
          onTogglePanel={onTogglePanel}
          isListOpen={isListOpen}
          onToggleList={onToggleList}
          onFlagToggle={onFlagToggle}
          hideControls={hideControls}
          showTextView={showTextView}
          showGridView={showGridView}
          isClearDisabled={isClearDisabled}
          hideClearSelection={hideClearSelection}
        />
        {hasError ? (
          <Box className={styles.errorContainer}>
            <Title
              variant="h2"
              size="sm"
              color={theme.colors.neutral[20]}
              marginTop={0}
              marginBottom={0}
              align="center"
            >
              {t("exam_failed_to_load_question_title")}
            </Title>
            <Text variant="body" color={theme.colors.neutral[30]}>
              {t("exam_failed_to_load_question")}
            </Text>
            <Text variant="body" color={theme.colors.neutral[30]}>
              {t("exam_failed_to_load_question_support")}
            </Text>
            <Text
              variant="body"
              color={theme.colors.error[40]}
              weight="emphasized"
            >
              {assetId}
            </Text>
          </Box>
        ) : (
          <Box className={styles.assetWrapper}>
            <AssetRenderer
              key={`${assetId}-${clearRevision}`}
              assetId={assetId}
              assetType="QUESTION"
              themeName={assetThemeName}
              colorScheme="light"
              authenticationToken={authenticationToken}
              authRequiredMessage={t("exam_auth_token_required")}
              env={env}
              assetTypeProps={assetTypeProps}
              fetchAssetFilter={fetchAssetFilter}
              onEvent={onEvent}
              onError={handleError}
              fallback={
                <Box className={styles.loadingContainer}>
                  <Spinner size="md" />
                  <Text
                    variant="body"
                    size="small"
                    color={theme.colors.neutral[50]}
                  >
                    {t("exam_loading_question")}
                  </Text>
                </Box>
              }
              errorFallback={(err) => (
                <Box className={styles.errorContainer}>
                  <Text
                    variant="body"
                    size="small"
                    color={theme.colors.error[40]}
                  >
                    {err}
                  </Text>
                </Box>
              )}
            />
          </Box>
        )}
      </Box>
    );
  },
);
